"use client";
import React, {createContext, useState, useEffect, useCallback} from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { slugify } from "../../utils/productUrl";

export const ProductContext = createContext(null);

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const [productsLoading, setProductsLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState(null);

  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categoryProductsLoading, setCategoryProductsLoading] = useState(false);

  /* ---------------- HELPER FUNCTION TO PARSE DESCRIPTION ---------------- */
  const parseDescription = (descriptionString) => {
    if (!descriptionString) return [];

    try {
      const parsed = JSON.parse(descriptionString);
      if (Array.isArray(parsed)) {
        return parsed.map(item => ({
          title: item.title || '',
          content: item.content || '',
          image: item.image ? `${CDN_URL}/${item.image}` : null
        }));
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  /* ---------------- SAFE NUMBER PARSING HELPER ---------------- */
  const safeParseNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }

    const parsed = Number(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  /* ---------------- PROCESS VARIATIONS HELPER ---------------- */
  const processVariations = (variations) => {
    if (!Array.isArray(variations)) return [];

    return variations.map(variation => ({
      id: variation.id,
      productId: variation.product_id,
      title: variation.title || '',
      description: variation.description || '',
      image: variation.image ? `${CDN_URL}/${variation.image}` : null,
      color: variation.color || null,
      material: variation.material || null,
      price: safeParseNumber(variation.price, null),
      offerPrice: variation.offer_price ? safeParseNumber(variation.offer_price, null) : null,
      size: variation.size || null,
      colorCode: variation.color_code || null,
      itemShape: variation.item_shape || null // Add item shape field for variations
    }));
  };

  /* ---------------- PROCESS CUSTOMISED BANNERS HELPER ---------------- */
  const processCustomisedBanners = (banners) => {
    if (!Array.isArray(banners)) return [];

    return banners.map(banner => ({
      image: banner.image ? `${CDN_URL}/${banner.image}` : null
    }));
  };

  /* ---------------- PROCESS BULK OFFERS HELPER ---------------- */
  const processBulkOffers = (bulkOffers) => {
    if (!Array.isArray(bulkOffers)) return [];

    return bulkOffers.map(offer => ({
      id: offer.id,
      productId: offer.product_id,
      minQuantity: safeParseNumber(offer.min_quantity, 1),
      maxQuantity: offer.max_quantity ? safeParseNumber(offer.max_quantity, null) : null,
      discountType: offer.discount_type || 'fixed',
      discountValue: safeParseNumber(offer.discount_value, 0),
      status: offer.status || 'Active'
    }));
  };

  /* ---------------- FETCH PRODUCT REVIEWS ---------------- */
  const fetchProductReviews = useCallback(async (productId) => {
    if (!productId) {
      return;
    }

    setReviewsLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/reviews/${productId}`);

      if (!res.ok) {
        throw new Error(`Failed to fetch reviews: ${res.status}`);
      }

      const data = await res.json();

      const reviewData = data?.data || {};

      // Extract and validate rating data with safe parsing
      const reviews = reviewData.reviews || [];
      const avgRating = safeParseNumber(reviewData.average_rating, 0);
      const totalCount = safeParseNumber(reviewData.total_reviews, 0);


      setReviews(reviews);
      setAverageRating(avgRating);
      setTotalReviews(totalCount);

      // If no reviews from API, try to calculate from reviews array
      if (totalCount === 0 && reviews.length > 0) {
        const calculatedAvg = reviews.reduce((sum, review) => {
          return sum + safeParseNumber(review.rating, 0);
        }, 0) / reviews.length;
        setAverageRating(calculatedAvg);
        setTotalReviews(reviews.length);
      }

    } catch (err) {
      setReviews([]);
      setAverageRating(0);
      setTotalReviews(0);
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  /* ---------------- FETCH ALL PRODUCTS ---------------- */
  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/products`);
      const data = await res.json();

      const list = data?.data || data?.products || [];

      if (!Array.isArray(list)) return;

      const mapped = list.map((p) => {
        let images = [];
        try {
          images = JSON.parse(p.product_images || "[]");
        } catch {}

        const fullImages = images.map(
          (img) => `${CDN_URL}/${img}`
        );

        // Handle video URL
        const videoUrl = p.video ? `${CDN_URL}/${p.video}` : null;

        // Format dimensions
        const formatDimensions = () => {
          const length = p.item_length_longer_edge;
          const lengthUnit = p.item_length_unit;
          const width = p.item_width_shorter_edge;
          const widthUnit = p.item_width_unit;

          if (length && width && lengthUnit && widthUnit) {
            return `${length}${lengthUnit} x ${width}${widthUnit}`;
          }
          return null;
        };

        return {
          id: String(p.id),
          name: p.product_name ?? "",
          description: p.description ?? "",
          descriptionData: parseDescription(p.description), // Parse description JSON
          // Fix price mappings: max_retail_price is original price, your_price is offer price
          price: safeParseNumber(p.max_retail_price, 0), // Original price
          offerPrice: safeParseNumber(p.your_price, 0), // Offer/Sale price
          categoryId: String(p.category_id),
          subcategoryId: String(p.subcategory_id),
          videoUrl: videoUrl,
          categoryName: p.category_name ?? "",
          subcategoryName: p.subcategory_name ?? "",
          image: fullImages[0] || "/placeholder.png",
          images: fullImages,
          video: videoUrl, // Add video field
          // Include rating data from API with safe parsing
          rating: safeParseNumber(p.rating, 0),
          ratingCount: safeParseNumber(p.rating_count || p.ratingCount, 0),
          averageRating: safeParseNumber(p.average_rating, 0), // Add average_rating from API
          // Add minimum order quantity field
          min_order_qty: safeParseNumber(p.min_order_qty, 1),
          minimumOrderQty: safeParseNumber(p.min_order_qty, 1), // Alternative field name for compatibility
          // New fields for specifications
          color: p.color || null,
          colorCode: p.color_code || null, // Add dynamic color code from API
          material: p.material || null,
          dimensions: formatDimensions(),
          size: p.size || null,
          itemShape: p.item_shape || null, // Add item shape field
          // Add return policy field
          product_policy: p.product_policy || null,
          return_status: p.return_status || null, // Add return status field from API
          // Add variations
          variations: processVariations(p.variations || []),
          // Add customisation data
          customisations: p.customisations || "No",
          customisedBanners: processCustomisedBanners(p.customised_banners || []),
          // Add bulk offers
          bulkOffers: processBulkOffers(p.bulk_offers || []),
          // SEO fields from admin
          metaTitle: p.meta_title || null,
          metaDescription: p.meta_description || null,
          urlSlug: p.url_slug || null,
        };
      });

      setProducts(mapped);
    } catch (err) {
    } finally {
      setProductsLoading(false);
    }
  }, []);

  /* ---------------- FETCH PRODUCT BY ID ---------------- */
  const fetchProductById = useCallback(async (id) => {
    if (!id) {
      setProductError("No product ID provided");
      setProduct(null);
      return;
    }

    setProductLoading(true);
    setProductError(null);

    // Reset product and rating data when fetching new product
    setProduct(null);
    setAverageRating(0);
    setTotalReviews(0);
    setReviews([]);

    try {
      const res = await fetch(`${BASE_URL}/product/${id}`);
      // Handle different response statuses
      if (!res.ok) {
        if (res.status === 404) {
          setProductError("Product not found");
          setProduct(null);
          return;
        } else if (res.status === 204) {
          setProductError("Product not found");
          setProduct(null);
          return;
        } else {
          setProductError(`Failed to fetch product: ${res.status} ${res.statusText}`);
          setProduct(null);
          return;
        }
      }

      const data = await res.json();

      // Check for API response indicating product not found
      if (data?.status === "204" && data?.message === "Product not found") {
        setProductError("Product not found");
        setProduct(null);
        return;
      }

      const p =
        data?.data && !Array.isArray(data.data)
          ? data.data
          : Array.isArray(data?.data)
          ? data.data[0]
          : data?.product;

      if (!p) {
        setProductError("Product not found");
        setProduct(null);
        return;
      }


      let images = [];
      try {
        images = JSON.parse(p.product_images || "[]");
      } catch {}

      const fullImages = images.map(
        (img) => `${CDN_URL}/${img}`
      );

      // Handle main image - prioritize main_image field, then first product image
      const mainImage = p.main_image
        ? `${CDN_URL}/${p.main_image}`
        : fullImages[0] || "/placeholder.png";

      // Handle video URL
      const videoUrl = p.video ? `${CDN_URL}/${p.video}` : null;

      // Format dimensions
      const formatDimensions = () => {
        const length = p.item_length_longer_edge;
        const lengthUnit = p.item_length_unit;
        const width = p.item_width_shorter_edge;
        const widthUnit = p.item_width_unit;

        if (length && width && lengthUnit && widthUnit) {
          return `${length}${lengthUnit} x ${width}${widthUnit}`;
        }
        return null;
      };

      const productData = {
        id: String(p.id),
        name: p.product_name ?? "",
        description: p.description ?? "",
        descriptionData: parseDescription(p.description), // Parse description JSON
        // Fix price mappings: max_retail_price is original price, your_price is offer price
        price: safeParseNumber(p.max_retail_price, 0), // Original price
        offerPrice: safeParseNumber(p.your_price, 0), // Offer/Sale price
        salePrice: safeParseNumber(p.sale_price, 0), // Sale price
        categoryId: String(p.category_name),
        image: mainImage,
        images: fullImages,
        video: videoUrl, // Add video field
        videoUrl: videoUrl, // Keep both for compatibility
        // Include rating data from product with safe parsing
        rating: safeParseNumber(p.rating, 0),
        ratingCount: safeParseNumber(p.rating_count || p.ratingCount, 0),
        // Include average_rating from API with safe parsing
        averageRating: safeParseNumber(p.average_rating, 0),
        // Add minimum order quantity field
        min_order_qty: safeParseNumber(p.min_order_qty, 1),
        minimumOrderQty: safeParseNumber(p.min_order_qty, 1), // Alternative field name for compatibility
        // New fields for specifications
        color: p.color || null,
        colorCode: p.color_code || null, // Add dynamic color code from API
        material: p.material || null,
        dimensions: formatDimensions(),
        size: p.size || null,
        itemShape: p.item_shape || null, // Add item shape field
        // Add return policy field
        product_policy: p.product_policy || null,
        return_status: p.return_status || null, // Add return status field from API
        // Add brand and model information
        brandName: p.brand_name || null,
        modelName: p.model_name || null,
        // Add variations
        variations: processVariations(p.variations || []),
        // Add customisation data
        customisations: p.customisations || "No",
        customisedBanners: processCustomisedBanners(p.customised_banners || []),
        // Add bulk offers
        bulkOffers: processBulkOffers(p.bulk_offers || []),
        // Add original product data for reference
        originalData: p
      };


      setProduct(productData);
      setProductError(null);

      // Fetch reviews and ratings for this product
      await fetchProductReviews(p.id);

    } catch (err) {
      setProductError("Failed to load product. Please try again.");
      setProduct(null);
    } finally {
      setProductLoading(false);
    }
  }, [fetchProductReviews]);

  /* ---------------- FETCH PRODUCTS BY CATEGORY ---------------- */
  const fetchProductsByCategory = useCallback(async (categoryId) => {
    if (!categoryId) {
      return [];
    }


    try {
      const res = await fetch(`${BASE_URL}/products/category/${categoryId}`);

      if (!res.ok) {
        throw new Error(`Failed to fetch category products: ${res.status}`);
      }

      const data = await res.json();
      const list = data?.data || [];

      if (!Array.isArray(list)) {
        return [];
      }

      const mapped = list.map((p) => {
        let images = [];
        try {
          images = JSON.parse(p.product_images || "[]");
        } catch {}

        const fullImages = images.map(
          (img) => `${CDN_URL}/${img}`
        );

        // Handle video URL
        const videoUrl = p.video ? `${CDN_URL}/${p.video}` : null;

        // Format dimensions
        const formatDimensions = () => {
          const length = p.item_length_longer_edge;
          const lengthUnit = p.item_length_unit;
          const width = p.item_width_shorter_edge;
          const widthUnit = p.item_width_unit;

          if (length && width && lengthUnit && widthUnit) {
            return `${length}${lengthUnit} x ${width}${widthUnit}`;
          }
          return null;
        };

        return {
          id: String(p.id),
          name: p.product_name ?? "",
          description: p.description ?? "",
          descriptionData: parseDescription(p.description), // Parse description JSON
          // Fix price mappings: max_retail_price is original price, your_price is offer price
          price: safeParseNumber(p.max_retail_price, 0), // Original price
          offerPrice: safeParseNumber(p.your_price, 0), // Offer/Sale price
          categoryId: String(p.category_id),
          subcategoryId: String(p.subcategory_id),
          videoUrl: videoUrl,
          categoryName: p.category_name ?? "",
          subcategoryName: p.subcategory_name ?? "",
          image: fullImages[0] || "/placeholder.png",
          images: fullImages,
          video: videoUrl, // Add video field
          // Include rating data from product with safe parsing
          rating: safeParseNumber(p.rating, 0),
          ratingCount: safeParseNumber(p.rating_count || p.ratingCount, 0),
          averageRating: safeParseNumber(p.average_rating, 0), // Add average_rating from API
          // Add minimum order quantity field
          min_order_qty: safeParseNumber(p.min_order_qty, 1),
          minimumOrderQty: safeParseNumber(p.min_order_qty, 1), // Alternative field name for compatibility
          // New fields for specifications
          color: p.color || null,
          colorCode: p.color_code || null, // Add dynamic color code from API
          material: p.material || null,
          dimensions: formatDimensions(),
          size: p.size || null,
          itemShape: p.item_shape || null, // Add item shape field
          // Add return policy field
          product_policy: p.product_policy || null,
          return_status: p.return_status || null, // Add return status field from API
          // Add variations
          variations: processVariations(p.variations || []),
          // Add customisation data
          customisations: p.customisations || "No",
          customisedBanners: processCustomisedBanners(p.customised_banners || []),
          // Add bulk offers
          bulkOffers: processBulkOffers(p.bulk_offers || []),
          // SEO fields from admin
          metaTitle: p.meta_title || null,
          metaDescription: p.meta_description || null,
          urlSlug: p.url_slug || null,
        };
      });

      return mapped;

    } catch (err) {
      return [];
    }
  }, []);

  /* ---------------- FETCH BY SLUG (resolves slug → numeric id) ---------------- */
  const fetchProductBySlug = useCallback(async (slug) => {
    if (!slug) {
      setProductError("No product identifier provided");
      setProduct(null);
      return;
    }

    // If slug looks like a numeric id, fetch directly
    if (/^\d+$/.test(slug)) {
      return fetchProductById(slug);
    }

    // Try to find the product in the already-loaded list
    // Priority: admin urlSlug → auto-generated name slug → none
    const found = products.find(p =>
      p.urlSlug === slug ||                     // admin-set slug
      slugify(p.name) === slug                  // auto-generated from name
    );
    if (found) {
      return fetchProductById(found.id);
    }

    // Fall back: hit the API (products not loaded yet)
    try {
      setProductLoading(true);
      setProductError(null);
      setProduct(null);

      const res  = await fetch(`${BASE_URL}/products`);
      const data = await res.json();
      const list = data?.data || [];
      const match = list.find(p =>
        p.url_slug === slug ||
        slugify(p.product_name) === slug
      );

      if (match) {
        return fetchProductById(String(match.id));
      } else {
        setProductError("Product not found");
        setProduct(null);
      }
    } catch {
      setProductError("Failed to load product");
    } finally {
      setProductLoading(false);
    }
  }, [fetchProductById, products]);

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return (
    <ProductContext.Provider
      value={{
        products,
        product,
        productsLoading,
        productLoading: productLoading,
        loading: productLoading, // Keep backward compatibility
        productError,
        error: productError, // Keep backward compatibility
        fetchProductById,
        fetchProductBySlug,
        fetchProducts, // Expose fetchProducts for manual refresh
        // ✅ category
        categoryProducts,
        categoryProductsLoading,
        fetchProductsByCategory,
        // ✅ reviews and ratings
        reviews,
        averageRating,
        totalReviews,
        reviewsLoading,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
